import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Select, message } from 'antd';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';
import { autoSizePromptTextarea, getPromptMinRows } from '../utils/promptTextarea';
import { isSpeechToText, paramDefault, paramOptions } from '../utils/mediaParams';

const IconSend = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M5 12h12M13 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconStop = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
);

const AudioGenForm = observer(({ prompt, onPromptChange }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const taRef = useRef(null);
    const fileRef = useRef(null);
    const model = chatStore.audioModels.find((m) => m.id === chatStore.settings.selected_audio_model);
    const stt = isSpeechToText(model);
    const params = model?.supported_parameters || {};
    const voices = useMemo(() => paramOptions(params, 'voices'), [params]);
    const formats = useMemo(() => {
        const opts = paramOptions(params, 'response_format');
        return opts.length ? opts : paramOptions(params, 'response_formats');
    }, [params]);
    const voice = chatStore.audioParams.voice || paramDefault(params, 'voices', voices[0]?.value || '');
    const responseFormat =
        chatStore.audioParams.response_format || paramDefault(params, 'response_format', formats[0]?.value || 'mp3');

    useEffect(() => {
        autoSizePromptTextarea(taRef.current);
    }, [prompt, stt]);

    useEffect(() => {
        if (stt) return;
        const patch = {};
        if (voices.length) {
            if (!voices.some((item) => item.value === chatStore.audioParams.voice)) {
                patch.voice = voices[0].value;
            }
        } else if (chatStore.audioParams.voice) {
            patch.voice = null;
        }
        if (formats.length) {
            if (!formats.some((item) => item.value === chatStore.audioParams.response_format)) {
                patch.response_format = formats[0].value;
            }
        } else if (chatStore.audioParams.response_format) {
            patch.response_format = null;
        }
        if (Object.keys(patch).length) chatStore.setAudioParams(patch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model?.id, stt, voices, formats]);

    const readyAudio = chatStore.attachments.filter(
        (a) => a.status === 'ready' && ((a.kind || '').includes('audio') || (a.mime || '').startsWith('audio/'))
    );

    const submit = async () => {
        if (chatStore.streaming) return;
        if (stt) {
            if (!readyAudio.length) {
                message.warning('Прикрепите аудиофайл');
                return;
            }
            await chatStore.transcribeAudio();
            return;
        }
        if (!String(prompt || '').trim()) return;
        await chatStore.generateSpeech(prompt.trim());
        onPromptChange('');
    };

    const controlClass = isDark
        ? '[&_.ant-select-selector]:!bg-white/5 [&_.ant-select-selector]:!border-white/10 [&_.ant-select-selection-item]:!text-gray-100'
        : '';

    return (
        <div
            className={`overflow-hidden rounded-2xl border shadow-sm ${
                isDark ? 'border-white/10 bg-[#2f2f2f]' : 'border-black/10 bg-white'
            }`}
        >
            <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac,.aac,.webm"
                onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach((file) => chatStore.uploadAttachment(file));
                    e.target.value = '';
                }}
            />
            {stt ? (
                <div className="flex items-center justify-between gap-2 px-3 py-3">
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className={`rounded-xl border px-3 py-2 text-sm ${
                            isDark ? 'border-white/15 hover:bg-white/10' : 'border-black/10 hover:bg-black/[0.04]'
                        }`}
                    >
                        {readyAudio.length ? `Файл: ${readyAudio[0].name}` : 'Выбрать аудиофайл'}
                    </button>
                    {chatStore.streaming ? (
                        <button
                            type="button"
                            onClick={() => chatStore.stop()}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white"
                        >
                            <IconStop />
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled={!readyAudio.length}
                            onClick={submit}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white disabled:opacity-35"
                        >
                            <IconSend />
                        </button>
                    )}
                </div>
            ) : (
                <div className="flex items-end gap-1.5 px-2 py-2 sm:gap-2 sm:px-3 sm:py-2.5">
                    <textarea
                        ref={taRef}
                        value={prompt}
                        onChange={(e) => {
                            onPromptChange(e.target.value);
                            autoSizePromptTextarea(e.target);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submit();
                            }
                        }}
                        rows={getPromptMinRows()}
                        placeholder={
                            model?.audio_kind === 'music' ? 'Опишите музыку…' : 'Текст для озвучки…'
                        }
                        className={`bananas-msg-font box-border min-w-0 flex-1 resize-none overflow-y-hidden bg-transparent px-2 py-2 leading-normal outline-none placeholder:opacity-45 ${
                            isDark ? 'text-gray-100' : 'text-gray-900'
                        }`}
                    />
                    <div className="mb-0.5 shrink-0">
                        {chatStore.streaming ? (
                            <button
                                type="button"
                                onClick={() => chatStore.stop()}
                                className="flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white"
                            >
                                <IconStop />
                            </button>
                        ) : (
                            <button
                                type="button"
                                disabled={!String(prompt || '').trim()}
                                onClick={submit}
                                className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white disabled:opacity-35"
                            >
                                <IconSend />
                            </button>
                        )}
                    </div>
                </div>
            )}
            {!stt && (voices.length > 0 || formats.length > 0) ? (
                <div
                    className={`flex flex-wrap items-center gap-2 border-t px-3 py-2 ${
                        isDark ? 'border-white/10' : 'border-black/8'
                    } ${controlClass}`}
                >
                    {voices.length > 0 && (
                        <Select
                            size="middle"
                            value={voice || undefined}
                            placeholder="Голос"
                            options={voices}
                            onChange={(v) => chatStore.setAudioParams({ voice: v })}
                            className="min-w-[140px]"
                            popupMatchSelectWidth={false}
                            getPopupContainer={() => document.body}
                        />
                    )}
                    {formats.length > 0 && (
                        <Select
                            size="middle"
                            value={responseFormat || undefined}
                            placeholder="Формат"
                            options={formats}
                            onChange={(v) => chatStore.setAudioParams({ response_format: v })}
                            className="min-w-[100px]"
                            popupMatchSelectWidth={false}
                            getPopupContainer={() => document.body}
                        />
                    )}
                </div>
            ) : null}
        </div>
    );
});

export default AudioGenForm;
